import { useMemo, useState } from 'react';
import { Loader } from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';

import { DialogOptions } from './types';

export interface DialogProps {
    open: boolean;
    onClose: () => void;
    onCancel?: () => Promise<void>;
    onConfirm: () => Promise<void>;
    options: DialogOptions;
}

export const DialogComponent = ({ open, options, onCancel, onConfirm, onClose }: DialogProps) => {
    const [waitingConfirmation, setWaitingConfirmation] = useState(false);
    const [waitingCancellation, setWaitingCancellation] = useState(false);

    const {
        title,
        description,
        content,
        confirmationText,
        confirmationLoadingText,
        cancellationText,
        cancellationLoadingText,
        allowClose,
        dialogProps,
        dialogContentProps,
        dialogFooterProps,
        confirmationButtonProps,
        cancellationButtonProps,
    } = options;

    const contentNode = useMemo(() => {
        if (typeof content === 'function') {
            return content(onClose);
        }
        return content;
    }, [content, onClose]);

    return (
        <Dialog open={open} onOpenChange={allowClose ? onClose : undefined} {...dialogProps}>
            <DialogContent {...dialogContentProps}>
                {(title || (description !== null && description !== undefined)) && (
                    <DialogHeader>
                        {title && <DialogTitle>{title}</DialogTitle>}
                        {description !== null && description !== undefined && (
                            <DialogDescription>{description}</DialogDescription>
                        )}
                    </DialogHeader>
                )}
                <div className="overflow-auto">{contentNode}</div>
                {(confirmationText || cancellationText) && (
                    <DialogFooter {...dialogFooterProps}>
                        {cancellationText && (
                            <Button
                                variant="outline"
                                {...cancellationButtonProps}
                                disabled={
                                    waitingConfirmation || waitingCancellation || cancellationButtonProps?.disabled
                                }
                                onClick={async (e) => {
                                    setWaitingCancellation(true);
                                    try {
                                        // Execute the custom onClick handler first if provided
                                        await cancellationButtonProps?.onClick?.(e);
                                        // Close the dialog after cancel action
                                        await onCancel?.();
                                    } catch (error) {
                                        console.error('Dialog cancellation failed:', error);
                                    } finally {
                                        setWaitingCancellation(false);
                                    }
                                }}
                            >
                                {waitingCancellation ? (
                                    <>
                                        <Loader className="animate-spin" />
                                        {cancellationLoadingText || cancellationText}
                                    </>
                                ) : (
                                    cancellationText
                                )}
                            </Button>
                        )}
                        {confirmationText && (
                            <Button
                                {...confirmationButtonProps}
                                disabled={
                                    waitingConfirmation || waitingCancellation || confirmationButtonProps?.disabled
                                }
                                onClick={async (e) => {
                                    setWaitingConfirmation(true);
                                    try {
                                        // Execute the custom onClick handler first (e.g., API call)
                                        await confirmationButtonProps?.onClick?.(e);
                                        // Only close the dialog if the onClick handler succeeds
                                        await onConfirm();
                                    } catch (error) {
                                        // Keep dialog open on error, onClick handler should handle error display
                                        console.error('Dialog confirmation failed:', error);
                                    } finally {
                                        setWaitingConfirmation(false);
                                    }
                                }}
                            >
                                {waitingConfirmation ? (
                                    <>
                                        <Loader className="animate-spin" />
                                        {confirmationLoadingText || confirmationText}
                                    </>
                                ) : (
                                    confirmationText
                                )}
                            </Button>
                        )}
                    </DialogFooter>
                )}
            </DialogContent>
        </Dialog>
    );
};
